import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { PromptProvider } from './context/PromptContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DebugProvider, useDebug } from './context/DebugContext';
import { ToastProvider } from './context/ToastContext';
import { setDebugLogger } from './services/api';
import Dashboard from './components/Dashboard';
import TemplateEditor from './components/TemplateEditor';
import CompositionMode from './components/CompositionMode';
import VariablesManager from './components/VariablesManager';
import Header from './components/Header';
import LandingPage from './components/LandingPage';
import AuthPage from './components/AuthPage';
import DebugPanel from './components/DebugPanel';
import GoogleAnalytics from './components/GoogleAnalytics';
import BottomNavigation from './components/BottomNavigation';
import './App.css';


// Loading Spinner Component
const LoadingScreen = () => (
    <div className="flex items-center justify-center min-h-screen bg-neo-bg">
        <div className="text-center">
            <div className="inline-block w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="font-bold uppercase tracking-widest">Loading Prompts DB...</p>
        </div>
    </div>
);

// Protected Route Wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) return <LoadingScreen />;
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  


  return children;
};

// Public Route Wrapper (redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();
    
    if (loading) return <LoadingScreen />;
    
    if (user) {
      return <Navigate to="/" replace />;
    }
  
    return children;
};

// Home Route (Landing for guests, Dashboard for users)
const HomeRoute = () => {
    const { user, loading } = useAuth();
    
    if (loading) return <LoadingScreen />;
    
    return user ? <Dashboard /> : <LandingPage />;
};

function App() {
  return (
    <DebugProvider>
      <DebugInitializer />
      <ToastProvider>
        <AuthProvider>
          <PromptProvider>
            <Router>
              <GoogleAnalytics />
              <div className="min-h-screen bg-gray-50 flex flex-col">
                <Header />
                <main className="flex-1 container mx-auto px-4 py-8 pb-bottom-nav">
                  <Routes>
                    {/* Root handles both Landing and Dashboard based on auth */}
                    <Route path="/" element={<HomeRoute />} />

                    {/* Auth Page */}
                    <Route path="/auth" element={
                        <PublicRoute>
                            <AuthPage />
                        </PublicRoute>
                    } />

                    {/* Protected Routes */}
                    <Route path="/editor/new" element={
                      <ProtectedRoute>
                        <TemplateEditor isCreate={true} />
                      </ProtectedRoute>
                    } />
                    <Route path="/editor/:id" element={
                      <ProtectedRoute>
                        <TemplateEditor />
                      </ProtectedRoute>
                    } />
                    <Route path="/compose" element={
                      <ProtectedRoute>
                        <CompositionMode />
                      </ProtectedRoute>
                    } />
                    <Route path="/variables" element={
                      <ProtectedRoute>
                        <VariablesManager />
                      </ProtectedRoute>
                    } />
                  </Routes>
                </main>
                <BottomNavigationWrapper />
              </div>
              <DebugPanel />
            </Router>
          </PromptProvider>
        </AuthProvider>
      </ToastProvider>
    </DebugProvider>
  );
}

const DebugInitializer = () => {
  const { addLog } = useDebug();

  React.useEffect(() => {
    setDebugLogger(addLog);
  }, [addLog]);

  return null;
};

const BottomNavigationWrapper = () => {
  const { user } = useAuth();
  const location = useLocation();

  // Hide bottom nav on editor pages to prevent overlap with editor controls
  const shouldHide = location.pathname.startsWith('/editor');

  return user && !shouldHide ? <BottomNavigation /> : null;
};

export default App;